import { Component } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIMultiColumn from 'UIComponents/layout/UIMultiColumn';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import { UserOptionType, TeamOptionType } from '../../types/OptionTypes';
import { getFlattenedTeamsMap, getSortedFlattenedTeamsList, getAllParentTeamIdsForChildTeamId } from '../../utils/TeamUtils';
import SelectedTeamOption from './SelectedTeamOption';
import SelectedUserOption from './SelectedUserOption';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
// Adds additional information used for displaying a team in the access status component.
const makeTeamOption = ({
  team,
  teams,
  teamMap,
  userHasTeamAssignmentScope,
  rootLevelTeamIds,
  useOnlyPrimaryMembers
}) => {
  return Object.assign({}, team, {
    totalTeamMembers: team.userIds.length + (useOnlyPrimaryMembers ? 0 : team.secondaryUserIds.length),
    fullParentNameList: getAllParentTeamIdsForChildTeamId(team.id, teams).map(parentId => teamMap[parentId].name),
    userCanRemoveTeam: userHasTeamAssignmentScope || !rootLevelTeamIds.includes(team.id)
  });
};
class UsersAndTeamsAccessStatus extends Component {
  constructor(...args) {
    super(...args);
    this.renderTeamOption = team => {
      const {
        onRemoveTeam
      } = this.props;
      const {
        id,
        name,
        totalTeamMembers,
        fullParentNameList,
        userCanRemoveTeam
      } = team;
      return /*#__PURE__*/_jsx(SelectedTeamOption, {
        id: id,
        name: name,
        totalTeamMembers: totalTeamMembers,
        fullParentNameList: fullParentNameList,
        onRemoveTeam: onRemoveTeam,
        userCanRemoveTeam: userCanRemoveTeam
      }, id);
    };
    this.renderUserOption = user => {
      const {
        onRemoveUser,
        disallowedUserTooltip,
        disabledUsers = [],
        selectedUserInfo
      } = this.props;
      const {
        id,
        email
      } = user;
      const canRemoveUser = !disabledUsers.includes(user.id);
      return /*#__PURE__*/_jsx(SelectedUserOption, {
        user: user,
        id: id,
        selectedUserInfo: selectedUserInfo,
        email: email,
        canRemoveUser: canRemoveUser,
        disallowedUserTooltip: disallowedUserTooltip,
        onRemoveUser: onRemoveUser
      }, id);
    };
  }
  getSelectedTeamOptions() {
    const {
      teams,
      selectedTeamIds,
      userHasTeamAssignmentScope,
      rootLevelTeamIds,
      useOnlyPrimaryMembers = false
    } = this.props;
    const teamMap = getFlattenedTeamsMap(teams);
    const teamList = getSortedFlattenedTeamsList(teams).filter(({
      id
    }) => selectedTeamIds.includes(id)).map(team => makeTeamOption({
      team,
      teams,
      teamMap,
      userHasTeamAssignmentScope,
      rootLevelTeamIds,
      useOnlyPrimaryMembers
    }));
    return teamList;
  }
  getExplicitlySelectedUserOptions() {
    const {
      users,
      userIdsForSelectedTeamIds,
      selectedUserIds
    } = this.props;
    return users.filter(user => {
      return selectedUserIds.includes(user.id) && !userIdsForSelectedTeamIds.includes(user.id);
    });
  }
  renderTitle() {
    const {
      hideTitle
    } = this.props;
    if (hideTitle === true) {
      return null;
    }
    return /*#__PURE__*/_jsx(UIFormLabel, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ui-asset-management-lib.labels.whoHasAccess"
      })
    });
  }
  render() {
    const {
      userHasTeamAssignmentScope,
      maxHeight = 200
    } = this.props;
    const teamOptions = this.getSelectedTeamOptions();
    const userOptions = this.getExplicitlySelectedUserOptions();
    if (teamOptions.length === 0 && userOptions.length === 0) {
      return null;
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [this.renderTitle(), /*#__PURE__*/_jsx(UIScrollContainer, {
        maxHeight: maxHeight,
        className: "m-bottom-4",
        children: /*#__PURE__*/_jsxs(UIMultiColumn, {
          children: [teamOptions.map(this.renderTeamOption), userHasTeamAssignmentScope && userOptions.map(this.renderUserOption)]
        })
      })]
    });
  }
}
UsersAndTeamsAccessStatus.propTypes = {
  users: PropTypes.arrayOf(UserOptionType).isRequired,
  teams: PropTypes.arrayOf(TeamOptionType).isRequired,
  selectedUserIds: PropTypes.array.isRequired,
  selectedTeamIds: PropTypes.array.isRequired,
  userIdsForSelectedTeamIds: PropTypes.array.isRequired,
  onRemoveUser: PropTypes.func.isRequired,
  onRemoveTeam: PropTypes.func.isRequired,
  rootLevelTeamIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  userHasTeamAssignmentScope: PropTypes.bool.isRequired,
  maxHeight: PropTypes.number,
  disallowedUserTooltip: PropTypes.string,
  disabledUsers: PropTypes.arrayOf(PropTypes.number),
  hideTitle: PropTypes.bool,
  useOnlyPrimaryMembers: PropTypes.bool
};
export default UsersAndTeamsAccessStatus;